import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Button,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import toast from "react-hot-toast";

import ClearDataService from "../../services/OrderingServices/ClearDataService";
import BreadCrumb from "../../Components/Common/BreadCrumb";

const ClearData = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const handleToggleModal = () => {
    setIsOpenModal(!isOpenModal);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleClearConfirmation = () => {
    if (!password.trim()) {
      toast.error("Please enter password");
      return;
    }
    setIsOpenModal(true);
  };

  const handleClearData = async () => {
    try {
      setLoading(true);

      // Call the service with password and takeBackup = false
      const response = await ClearDataService.clearAllData(password, false);

      if (response) {
        toast.success("All data cleared successfully!");
        setPassword("");
        setIsOpenModal(false);

        // Reset to landing page after successful clear
        setTimeout(() => {
          navigate("/dashboard");
        }, 1500);
      }
    } catch (error) {
      console.error("Error clearing data:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to clear data. Please check your password and try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb pageTitle="Settings" title="Clear Data" />
      </div>
      <Row>
        <Col
          lg={12}
          className="d-flex justify-content-center align-content-center align-items-center mt-0 mt-lg-5 "
        >
          <Card className="col-12 col-lg-6 col-md-6">
            <CardHeader className="d-flex align-items-center">
              <h4 className="mb-0">Clear All Data</h4>
            </CardHeader>
            <CardBody>
              <Alert color="danger" className="mb-4">
                <h5 className="alert-heading">
                  <i className="bx bx-exclamation-circle"></i> WARNING
                </h5>
                <p className="mb-0">
                  This action will permanently delete all data from the system.
                  Make sure you have a backup before proceeding. This action
                  cannot be undone!
                </p>
              </Alert>

              <Form>
                <FormGroup>
                  <Label for="password" className="form-label">
                    Enter Admin Password <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="password"
                    id="password"
                    placeholder="Enter your admin password"
                    value={password}
                    onChange={handlePasswordChange}
                    disabled={loading}
                  />
                  <small className="form-text text-muted">
                    Your password is required to confirm this action.
                  </small>
                </FormGroup>

                <div className="d-flex gap-2">
                  <Button
                    color="danger"
                    onClick={handleClearConfirmation}
                    disabled={loading || !password.trim()}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Clearing...
                      </>
                    ) : (
                      <>
                        <i className="bx bx-trash me-2"></i>
                        Clear All Data
                      </>
                    )}
                  </Button>

                  <Button
                    color="secondary"
                    onClick={() => {
                      setPassword("");
                      navigate(-1);
                    }}
                    disabled={loading}
                  >
                    <i className="bx bx-x me-2"></i>
                    Cancel
                  </Button>
                </div>
              </Form>

              <Modal isOpen={isOpenModal} toggle={handleToggleModal} centered>
                <ModalHeader toggle={handleToggleModal}>
                  <i className="bx bx-exclamation-circle text-danger me-2"></i>
                  Confirm Clear All Data
                </ModalHeader>
                <ModalBody>
                  <Alert color="danger" className="mb-3">
                    <h5 className="alert-heading">
                      <i className="bx bx-exclamation-triangle"></i> Warning
                    </h5>
                    <p className="mb-0">
                      <strong>Are you sure?</strong> This will clear all data
                      without backup. This action is irreversible!
                    </p>
                  </Alert>
                </ModalBody>
                <ModalFooter>
                  <Button
                    color="secondary"
                    onClick={handleToggleModal}
                    disabled={loading}
                  >
                    <i className="bx bx-x me-2"></i>
                    Cancel
                  </Button>
                  <Button
                    color="danger"
                    onClick={handleClearData}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Clearing...
                      </>
                    ) : (
                      <>
                        <i className="bx bx-check me-2"></i>
                        Confirm & Clear
                      </>
                    )}
                  </Button>
                </ModalFooter>
              </Modal>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ClearData;
